import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash, Eye, EyeSlash, ArrowUp, ArrowDown, ArrowSquareOut, Archive, Clock,
} from '@phosphor-icons/react';
import { api } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { RecognitionEditorDialog } from '../../components/admin/recognition/RecognitionEditorDialog';

const statusTone = {
  published: 'bg-emerald-50 text-emerald-700',
  scheduled: 'bg-amber-50 text-amber-700',
  draft: 'bg-black/[0.05] text-foreground-muted',
};

const formatMoment = (iso, timeZone) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium', timeStyle: 'short', timeZone: timeZone || 'Europe/Lisbon',
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
};

const AdminRecognitionPage = () => {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const [listRes, catsRes] = await Promise.all([
        api.get(`/admin/recognition?include_archived=${showArchived}`),
        api.get('/admin/recognition/categories'),
      ]);
      setEntries(listRes.data);
      setCategories(catsRes.data.categories);
    } catch {
      toast.error('Failed to load recognition entries');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/admin/recognition/${editing.id}`, form);
        toast.success('Recognition updated');
      } else {
        await api.post('/admin/recognition', form);
        toast.success('Recognition added');
      }
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
      throw error;
    }
  };

  const patch = async (entry, body, message) => {
    try {
      await api.put(`/admin/recognition/${entry.id}`, body);
      toast.success(message);
      load();
    } catch {
      toast.error('Failed to update');
    }
  };

  const move = async (index, direction) => {
    const next = [...entries];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setEntries(next);
    try {
      await api.put('/admin/recognition/order', { ids: next.map((e) => e.id) });
    } catch {
      toast.error('Failed to reorder');
      load();
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete "${entry.organization_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/recognition/${entry.id}`);
      toast.success('Recognition deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const liveCount = entries.filter((e) => e.is_live && e.featured_home && !e.archived).length;

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="admin-recognition-page">
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-heading font-semibold">Recognition &amp; Media</h1>
            <p className="text-sm text-foreground-muted">
              The &ldquo;Recognized &amp; Featured By&rdquo; strip under the homepage hero · {liveCount} live
              {liveCount > 6 ? ' (only the first six show)' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowArchived((v) => !v)}
              className="border-black/10"
              data-testid="toggle-archived-button"
            >
              <Archive size={17} className="mr-2" />
              {showArchived ? 'Hide archived' : 'Show archived'}
            </Button>
            <Button
              onClick={() => { setEditing(null); setEditorOpen(true); }}
              className="bg-primary hover:bg-primary-hover"
              data-testid="new-recognition-button"
            >
              <Plus size={17} className="mr-2" /> New recognition
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-7 w-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <Card className="p-12 text-center border-black/[0.06]">
            <p className="text-sm text-foreground-muted">
              No recognition entries yet. Add the first organization to light up the homepage strip.
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {entries.map((entry, index) => (
              <Card
                key={entry.id}
                className="p-4 border-black/[0.06] flex items-center gap-4"
                data-testid={`recognition-row-${entry.id}`}
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="p-1 rounded hover:bg-black/[0.04] disabled:opacity-25"
                    aria-label="Move up"
                    data-testid={`recognition-up-${entry.id}`}
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === entries.length - 1}
                    className="p-1 rounded hover:bg-black/[0.04] disabled:opacity-25"
                    aria-label="Move down"
                    data-testid={`recognition-down-${entry.id}`}
                  >
                    <ArrowDown size={13} />
                  </button>
                </div>

                <div className="w-28 h-10 flex items-center justify-center bg-black/[0.02] rounded-lg flex-shrink-0">
                  {entry.logo_url ? (
                    <img src={entry.logo_url} alt="" className="max-h-7 max-w-[88px] object-contain" />
                  ) : (
                    <span className="text-[10px] text-foreground-muted">No logo</span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium truncate">{entry.organization_name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${statusTone[entry.status]}`}>
                      {entry.status}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] bg-black/[0.04] text-foreground-muted">
                      {entry.category_label}
                    </span>
                    {entry.is_live && entry.featured_home && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-primary/10 text-primary">
                        on homepage
                      </span>
                    )}
                  </div>
                  {entry.description && (
                    <p className="text-xs text-foreground-muted mt-1 truncate">{entry.description}</p>
                  )}
                  {entry.status === 'scheduled' && entry.publish_at && (
                    <p className="text-xs text-amber-700 mt-1 flex items-center gap-1.5">
                      <Clock size={13} />
                      Goes live {formatMoment(entry.publish_at, entry.publish_timezone)} ({entry.publish_timezone})
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {entry.reference_url && (
                    <a
                      href={entry.reference_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg hover:bg-black/[0.04]"
                      title="Open source"
                      data-testid={`recognition-open-${entry.id}`}
                    >
                      <ArrowSquareOut size={16} />
                    </a>
                  )}
                  <button
                    onClick={() => patch(
                      entry,
                      { status: entry.status === 'published' ? 'draft' : 'published' },
                      entry.status === 'published' ? 'Unpublished' : 'Published',
                    )}
                    className="p-2 rounded-lg hover:bg-black/[0.04]"
                    title={entry.status === 'published' ? 'Unpublish' : 'Publish now'}
                    data-testid={`recognition-toggle-${entry.id}`}
                  >
                    {entry.status === 'published' ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    onClick={() => patch(entry, { archived: !entry.archived }, entry.archived ? 'Restored' : 'Archived')}
                    className="p-2 rounded-lg hover:bg-black/[0.04]"
                    title={entry.archived ? 'Restore' : 'Archive'}
                    data-testid={`recognition-archive-${entry.id}`}
                  >
                    <Archive size={16} className={entry.archived ? 'text-primary' : ''} />
                  </button>
                  <button
                    onClick={() => { setEditing(entry); setEditorOpen(true); }}
                    className="p-2 rounded-lg hover:bg-black/[0.04]"
                    title="Edit"
                    data-testid={`recognition-edit-${entry.id}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => remove(entry)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                    title="Delete"
                    data-testid={`recognition-delete-${entry.id}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <RecognitionEditorDialog
        open={editorOpen}
        entry={editing}
        categories={categories}
        onClose={() => setEditorOpen(false)}
        onSave={save}
      />
    </div>
  );
};

export default AdminRecognitionPage;
